/**
 * Generation Progress Component
 *
 * Displays real-time progress of creature generation:
 * - Queued → Processing → Complete/Failed
 * - Progress bar (0-100%)
 * - Status messages
 * - Final result display
 */

import React, { useEffect, useState } from 'react';
import { pollJobStatus, type JobStatus, type GenerationResult } from '@/services/generationService';
import { getCreatureLibraryService } from '@/services/creatureLibraryService';
import { AnimationDebugger } from '@/components/AnimationDebugger';
import { CombatAttributeDisplay } from '@/components/CombatAttributeDisplay';
import { PixelMorphAnimation } from '@/components/PixelMorphAnimation';
import { EmergenceOverlay } from '@/components/EmergenceOverlay';
import styles from './GenerationProgress.module.css';

interface GenerationProgressProps {
  jobId: string;
  onComplete?: (result: GenerationResult) => void;
  onError?: (error: string) => void;
}

// Local storage key for persisting job ID - exported for use by other components
export const GENERATION_JOB_KEY = 'generation_in_progress_job';

/**
 * Get persisted job ID from localStorage (for recovery after browser refresh)
 */
export function getPersistedJobId(): string | null {
  return localStorage.getItem(GENERATION_JOB_KEY);
}

/**
 * Clear persisted job ID from localStorage
 */
export function clearPersistedJobId(): void {
  localStorage.removeItem(GENERATION_JOB_KEY);
}

export const GenerationProgress: React.FC<GenerationProgressProps> = ({
  jobId,
  onComplete,
  onError
}) => {
  const [status, setStatus] = useState<JobStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPolling, setIsPolling] = useState(false);

  // Store job ID in localStorage for recovery after refresh
  useEffect(() => {
    if (jobId) {
      localStorage.setItem(GENERATION_JOB_KEY, jobId);
    }
    return () => {
      // Clean up on unmount (but not on refresh - that's handled by completion)
    };
  }, [jobId]);

  // Polling function - extracted so it can be called for resume
  const startPolling = async () => {
    if (isPolling) return;

    setIsPolling(true);
    setError(null);

    try {
      const finalStatus = await pollJobStatus(
        jobId,
        (currentStatus) => {
          // Update UI with each poll
          setStatus(currentStatus);
        },
        {
          pollInterval: 2500, // 2.5 seconds
          maxAttempts: 360 // 15 minutes total (360 * 2.5s = 900s)
        }
      );

      // Handle final state
      if (finalStatus.status === 'completed' && finalStatus.result) {
        // Clear localStorage on successful completion
        localStorage.removeItem(GENERATION_JOB_KEY);
        if (onComplete) {
          onComplete(finalStatus.result);
        }
      } else if (finalStatus.status === 'failed') {
        const errorMessage = finalStatus.error?.message || 'Generation failed';
        // Keep partialAssets in status for error display
        setStatus(finalStatus);
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
      }
    } catch (err: any) {
      const errorMessage = err.message || 'An unexpected error occurred';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsPolling(false);
    }
  };

  useEffect(() => {
    // Start polling when component mounts
    startPolling();
  }, [jobId]); // Only depend on jobId, not callbacks

  if (error) {
    // Check if we have partial assets to display
    const hasPartialSprite = status?.partialAssets?.baseSprite;

    return (
      <div className={styles.containerDark}>
        <div className={styles.errorCardDark}>
          {/* Show partial sprite if available */}
          {hasPartialSprite && (
            <div className={styles.errorSpriteContainer}>
              <EmergenceOverlay
                sprite={status.partialAssets!.baseSprite}
                progress={status.progress || 0}
                statusMessage="Generation failed"
                size="medium"
                isComplete={false}
              />
            </div>
          )}

          <div className={styles.errorContent}>
            <div className={styles.errorIconDark}>
              {hasPartialSprite ? '⚠️' : '❌'}
            </div>
            <h2 className={styles.errorTitleDark}>Generation Failed</h2>
            <p className={styles.errorMessageDark}>{error}</p>

            {hasPartialSprite && (
              <p className={styles.errorProgressDark}>
                Failed at {status.progress}% progress
              </p>
            )}

            <div className={styles.errorActionsDark}>
              {/* Show Resume button if it's a polling timeout (not a backend failure) */}
              {error.includes('timed out') || error.includes('polling') ? (
                <button
                  className={styles.resumeButtonDark}
                  onClick={startPolling}
                  disabled={isPolling}
                >
                  {isPolling ? 'Checking...' : 'Resume Polling'}
                </button>
              ) : null}
              <button
                className={styles.retryButtonDark}
                onClick={() => window.location.href = '/create'}
              >
                Try Again
              </button>
              <button
                className={styles.homeButtonDark}
                onClick={() => window.location.href = '/gallery'}
              >
                Go to Gallery
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!status) {
    return (
      <div className={styles.containerDark}>
        <div className={styles.animationCard}>
          <h2 className={styles.titleGlow}>Creating Your Creature</h2>
          <div className={styles.animationWrapper}>
            <PixelMorphAnimation
              width={350}
              height={350}
              pixelSize={6}
              holdDuration={2500}
              morphDuration={1200}
            />
          </div>
          <p className={styles.statusTextGlow}>Connecting to generation service...</p>
        </div>
      </div>
    );
  }

  // Render different UI based on status
  if (status.status === 'completed' && status.result) {
    return (
      <div className={styles.container}>
        <div className={styles.successCard}>
          <div className={styles.successIcon}>✨</div>
          <h2 className={styles.successTitle}>Generation Complete!</h2>
          <div className={styles.resultPanel}>
            <ResultDisplay result={status.result} jobId={jobId} />
          </div>
        </div>
      </div>
    );
  }

  // Show progress for queued/processing states
  const statusText = status.status === 'queued'
    ? 'Waiting in queue...'
    : status.message || 'Processing your creature...';

  const progressSteps = [
    { label: 'Queued', threshold: 0 },
    { label: 'Generating Base', threshold: 20 },
    { label: 'Analyzing', threshold: 40 },
    { label: 'Creating Animations', threshold: 70 },
    { label: 'Finalizing', threshold: 90 },
    { label: 'Complete', threshold: 100 }
  ];

  const currentStep = progressSteps.findIndex(
    step => status.progress < step.threshold
  );
  const activeStepIndex = currentStep === -1 ? progressSteps.length - 1 : Math.max(0, currentStep - 1);

  // Check if we have partial assets (sprite available for progressive loading)
  const hasPartialAssets = Boolean(status.partialAssets?.baseSprite);

  // Get current step label for status message
  const currentStepLabel = progressSteps[activeStepIndex]?.label || 'Processing';

  return (
    <div className={styles.containerDark}>
      <div className={styles.animationCard}>
        <h2 className={styles.titleGlow}>Creating Your Creature</h2>

        {/* Animation Display - Switch between PixelMorph and EmergenceOverlay */}
        <div className={styles.animationWrapper}>
          {hasPartialAssets ? (
            // Show actual sprite with emergence overlay once available
            <EmergenceOverlay
              sprite={status.partialAssets!.baseSprite}
              progress={status.progress}
              statusMessage={currentStepLabel}
              size="large"
              isComplete={false}
            />
          ) : (
            // Show abstract pixel animation while waiting for sprite
            <PixelMorphAnimation
              width={350}
              height={350}
              pixelSize={6}
              holdDuration={2500}
              morphDuration={1200}
            />
          )}
        </div>

        <p className={styles.statusTextGlow}>
          {hasPartialAssets
            ? `Your creature is materializing... ${currentStepLabel}`
            : statusText
          }
        </p>

        {/* Progress Bar */}
        <div className={styles.progressBarDark}>
          <div
            className={styles.progressFillGlow}
            style={{ width: `${status.progress}%` }}
          >
            <span className={styles.progressTextGlow}>{status.progress}%</span>
          </div>
        </div>

        {/* Step Indicators */}
        <div className={styles.stepsDark}>
          {progressSteps.map((step, index) => (
            <div
              key={step.label}
              className={`${styles.stepDark} ${
                index <= activeStepIndex ? styles.stepActiveDark : ''
              } ${index === activeStepIndex ? styles.stepCurrentDark : ''}`}
            >
              <span className={styles.stepIconDark}>
                {index < activeStepIndex ? '✓' : index === activeStepIndex ? '●' : '○'}
              </span>
              <span className={styles.stepLabelDark}>{step.label}</span>
            </div>
          ))}
        </div>

        {/* Info Panel */}
        <div className={styles.infoPanelDark}>
          <p className={styles.infoTextDark}>
            <strong>Job ID:</strong> {jobId}
          </p>
          <p className={styles.infoTextDark}>
            {hasPartialAssets
              ? 'Generating directional views and animations...'
              : 'This process typically takes 2-3 minutes. Your creature will have 20+ custom animations across 6 directions!'
            }
          </p>
        </div>

        {/* Create Another Creature Prompt */}
        <div className={styles.createAnotherPrompt}>
          <div className={styles.promptContent}>
            <span className={styles.promptIcon}>✨</span>
            <div className={styles.promptText}>
              <p className={styles.promptTitle}>While you wait...</p>
              <p className={styles.promptMessage}>
                You can create up to 3 creatures at once! Why not start another?
              </p>
            </div>
            <button
              className={styles.createAnotherButton}
              onClick={() => window.location.href = '/create'}
            >
              Create Another
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Result Display Component
 *
 * Shows the generated creature's details
 */
const ResultDisplay: React.FC<{ result: GenerationResult; jobId: string }> = ({ result, jobId }) => {
  // Animation frame playback
  const [currentFrame, setCurrentFrame] = React.useState(0);

  // Save to library state
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveSuccess, setSaveSuccess] = React.useState(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [savedCreatureId, setSavedCreatureId] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!result.animationFrames || result.animationFrames.length === 0) {
      return;
    }

    const interval = setInterval(() => {
      setCurrentFrame((prev) => (prev + 1) % result.animationFrames!.length);
    }, 100); // 10 FPS animation

    return () => clearInterval(interval);
  }, [result.animationFrames]);

  // Handle save to library
  const handleSaveToLibrary = async () => {
    setIsSaving(true);
    setSaveError(null);

    try {
      const libraryService = getCreatureLibraryService();
      const response = await libraryService.saveCreature({ jobId });

      setSaveSuccess(true);
      setSavedCreatureId(response.creatureId);
      console.log('Creature saved successfully:', response);
    } catch (error) {
      console.error('Error saving creature:', error);
      setSaveError(error instanceof Error ? error.message : 'Failed to save creature');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className={styles.resultContainer}>
      {/* Save to Library Actions */}
      <div className={styles.resultSection}>
        <h3 className={styles.resultSectionTitle}>Save to Library</h3>
        {!saveSuccess ? (
          <div className={styles.saveControls}>
            <button
              className={styles.primaryButton}
              onClick={handleSaveToLibrary}
              disabled={isSaving}
            >
              {isSaving ? 'Saving...' : 'Save to Library'}
            </button>
            {saveError && (
              <p className={styles.errorText}>{saveError}</p>
            )}
          </div>
        ) : (
          <div className={styles.saveSuccess}>
            <p className={styles.successText}>
              ✓ Creature saved successfully!
            </p>
            <p className={styles.creatureIdText}>
              Creature ID: {savedCreatureId}
            </p>
            <div className={styles.actions}>
              <button
                className={styles.secondaryButton}
                onClick={() => window.location.href = '/create'}
              >
                Create Another
              </button>
              <button
                className={styles.primaryButton}
                onClick={() => window.location.href = `/deployment?creatures=${savedCreatureId}`}
              >
                Deploy to Battle
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Animated Sprite - Just shows the sprite, no controls */}
      {result.animationFrames && result.animationFrames.length > 0 ? (
        <div className={styles.resultSection}>
          <h3 className={styles.resultSectionTitle}>Your Creature</h3>
          <div className={styles.spriteDisplay}>
            <img
              src={`data:image/png;base64,${result.animationFrames[currentFrame]}`}
              alt="Animated creature sprite"
              className={styles.spriteImage}
              style={{
                imageRendering: 'pixelated',
                width: '256px',
                height: '256px',
                border: '2px solid rgba(196, 150, 58, 0.2)',
                borderRadius: '8px',
                backgroundColor: '#0C0B09'
              }}
            />
            <p className={styles.animationCount}>
              {result.animations.totalAnimations} animations assigned
            </p>
          </div>
        </div>
      ) : result.spriteImageBase64 ? (
        <div className={styles.resultSection}>
          <h3 className={styles.resultSectionTitle}>Your Creature</h3>
          <div className={styles.spriteDisplay}>
            <img
              src={`data:image/png;base64,${result.spriteImageBase64}`}
              alt="Generated sprite"
              className={styles.spriteImage}
              style={{
                imageRendering: 'pixelated',
                width: '256px',
                height: '256px',
                border: '2px solid rgba(196, 150, 58, 0.2)',
                borderRadius: '8px',
                backgroundColor: '#0C0B09'
              }}
            />
          </div>
        </div>
      ) : null}

      {/* Creature Concept */}
      {result.claudeAnalysis && (
        <div className={styles.resultSection}>
          <h3 className={styles.resultSectionTitle}>Creature Analysis</h3>
          <div className={styles.resultGrid}>
            <div className={styles.resultItem}>
              <span className={styles.resultLabel}>Concept:</span>
              <span className={styles.resultValue}>{result.claudeAnalysis.concept}</span>
            </div>
            <div className={styles.resultItem}>
              <span className={styles.resultLabel}>Race:</span>
              <span className={styles.resultValue}>{result.claudeAnalysis.race}</span>
            </div>
            <div className={styles.resultItem}>
              <span className={styles.resultLabel}>Class:</span>
              <span className={styles.resultValue}>{result.claudeAnalysis.class}</span>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      {result.claudeAnalysis?.stats && (
        <div className={styles.resultSection}>
          <h3 className={styles.resultSectionTitle}>Stats</h3>
          <div className={styles.statsGrid}>
            <StatBar label="Health" value={result.claudeAnalysis.stats.health} max={200} />
            <StatBar label="Attack" value={result.claudeAnalysis.stats.attack} max={50} />
            <StatBar label="Defense" value={result.claudeAnalysis.stats.defense} max={30} />
            <StatBar label="Speed" value={result.claudeAnalysis.stats.speed} max={10} />
          </div>
        </div>
      )}

      {/* Admin Animation Debugger - Hidden by default, click to expand */}
      {result.animations && (
        <AnimationDebugger
          baseSprite={result.spriteImageBase64}
          walkAnimationFrames={result.animationFrames}
          animationSet={result.animations.animationSet}
          totalAnimations={result.animations.totalAnimations}
          defaultExpanded={false}
        />
      )}

      {/* Combat Attributes Display - Shows baseline attack + AI-assigned special attributes */}
      {result.combatAttributes && result.combatAttributes.attributes.length > 0 && result.spriteImageBase64&& result.baselineAttackType && (
        <div className={styles.resultSection} style={{ marginTop: '30px' }}>
          <CombatAttributeDisplay
            attributes={result.combatAttributes.attributes}
            creatureSprite={result.spriteImageBase64}
            baselineAttackType={result.baselineAttackType}
          />
        </div>
      )}

      {/* Style Validation */}
      {result.styleValidation && (
        <div className={styles.resultSection}>
          <h3 className={styles.resultSectionTitle}>Style Preservation</h3>
          <div className={styles.styleValidation}>
            <div className={styles.styleScore}>
              <span className={styles.scoreLabel}>Overall Score:</span>
              <span className={`${styles.scoreValue} ${result.styleValidation.passed ? styles.scorePassed : styles.scoreFailed}`}>
                {Math.round(result.styleValidation.overallScore * 100)}%
              </span>
            </div>
            <p className={styles.styleFeedback}>{result.styleValidation.feedback}</p>
          </div>
        </div>
      )}

      {/* Processing Time */}
      <div className={styles.resultSection}>
        <p className={styles.processingTime}>
          Generated in {(result.processingTimeMs / 1000).toFixed(1)} seconds
        </p>
      </div>
    </div>
  );
};

/**
 * Stat Bar Component
 */
const StatBar: React.FC<{ label: string; value: number; max: number }> = ({ label, value, max }) => {
  const percentage = Math.min((value / max) * 100, 100);

  return (
    <div className={styles.statBar}>
      <div className={styles.statHeader}>
        <span className={styles.statLabel}>{label}</span>
        <span className={styles.statValue}>{value}</span>
      </div>
      <div className={styles.statTrack}>
        <div
          className={styles.statFill}
          style={{ width: `${percentage}%` }}
        ></div>
      </div>
    </div>
  );
};
